import React from "react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism"

import globalStyles from "../../../../App.module.scss"

const registrySecretScript = `#!/usr/bin/env bash
# Wrapper around kubectl to create secrets for custom container registry.
# https://kubernetes.io/docs/tasks/configure-pod-container/pull-image-private-registry/
# kubectl create secret docker-registry regcred --docker-server=$1 --docker-username=$2 --docker-password=$3 --docker-email=$4
#
# The secret is built from a generated .dockerconfigjson and piped to kubectl,
# so the password never shows up in argv (ps, /proc) or in shell history.
# Re-running is safe: the secret is applied, not created, so it gets updated.

set -euo pipefail

SECRET_NAME="regcred"
NAMESPACE="default"
SERVER=""
USERNAME=""
PASSWORD="\${REGISTRY_PASSWORD:-}"
EMAIL=""
CONTEXT=""
PRINT_ONLY=false
PATCH_SA=""

usage() {
  cat <<EOF
Usage: $(basename "$0") [options] [server] [username] [password] [email]

Options:
  -s SERVER     Registry server (e.g. registry.example.com:5000)
  -u USERNAME   Registry username
  -p PASSWORD   Registry password (avoid: prefer REGISTRY_PASSWORD env var,
                stdin, or the interactive prompt)
  -P            Read the password from stdin (e.g. from a password manager)
  -e EMAIL      Email (optional, kept for compatibility)
  -N NAME       Secret name (default: \${SECRET_NAME})
  -n NAMESPACE  Target namespace (default: \${NAMESPACE})
  -c CONTEXT    kubectl context to use (default: current context)
  -a SA         Also add the secret to imagePullSecrets of this service account
  -o            Only print the Secret YAML, do not apply it
  -h            Show this help

Examples:
  $(basename "$0") -s registry.lab.local -u julian -n myapp
  pass show registry | $(basename "$0") -s registry.lab.local -u julian -P -a default
  $(basename "$0") registry.lab.local julian 's3cret' me@example.com
EOF
}

die() { echo "Error: $*" >&2; exit 1; }

READ_STDIN=false
while getopts ":s:u:p:Pe:N:n:c:a:oh" opt; do
  case "$opt" in
    s) SERVER="$OPTARG" ;;
    u) USERNAME="$OPTARG" ;;
    p) PASSWORD="$OPTARG" ;;
    P) READ_STDIN=true ;;
    e) EMAIL="$OPTARG" ;;
    N) SECRET_NAME="$OPTARG" ;;
    n) NAMESPACE="$OPTARG" ;;
    c) CONTEXT="$OPTARG" ;;
    a) PATCH_SA="$OPTARG" ;;
    o) PRINT_ONLY=true ;;
    h) usage; exit 0 ;;
    :) die "option -$OPTARG needs a value" ;;
    \\?) die "unknown option -$OPTARG (see -h)" ;;
  esac
done
shift $((OPTIND - 1))

# Positional fallback, matching the original $1..$4 usage.
SERVER="\${SERVER:-\${1:-}}"
USERNAME="\${USERNAME:-\${2:-}}"
PASSWORD="\${PASSWORD:-\${3:-}}"
EMAIL="\${EMAIL:-\${4:-}}"

command -v kubectl >/dev/null 2>&1 || die "kubectl not found in PATH"
command -v base64  >/dev/null 2>&1 || die "base64 not found in PATH"

[[ -n "$SERVER"   ]] || { usage >&2; die "registry server is required"; }
[[ -n "$USERNAME" ]] || { usage >&2; die "username is required"; }

if $READ_STDIN; then
  IFS= read -r PASSWORD || true
elif [[ -z "$PASSWORD" ]]; then
  [[ -t 0 ]] || die "no password given and stdin is not a terminal (use -P)"
  read -r -s -p "Password for \${USERNAME}@\${SERVER}: " PASSWORD
  echo >&2
fi
[[ -n "$PASSWORD" ]] || die "password is empty"

KUBECTL=(kubectl)
[[ -n "$CONTEXT" ]] && KUBECTL+=(--context "$CONTEXT")

# Minimal JSON string escaping for server, username and email.
json_escape() {
  local s="$1"
  s="\${s//\\\\/\\\\\\\\}"
  s="\${s//\\"/\\\\\\"}"
  printf '%s' "$s"
}

b64() { printf '%s' "$1" | base64 | tr -d '\\n'; }

AUTH="$(b64 "\${USERNAME}:\${PASSWORD}")"
unset PASSWORD

build_config() {
  printf '{"auths":{"%s":{"username":"%s","auth":"%s"%s}}}' \\
    "$(json_escape "$SERVER")" \\
    "$(json_escape "$USERNAME")" \\
    "$AUTH" \\
    "$( [[ -n "$EMAIL" ]] && printf ',"email":"%s"' "$(json_escape "$EMAIL")" )"
}

render_secret() {
  build_config | "\${KUBECTL[@]}" create secret generic "$SECRET_NAME" \\
    --namespace "$NAMESPACE" \\
    --type kubernetes.io/dockerconfigjson \\
    --from-file=.dockerconfigjson=/dev/stdin \\
    --dry-run=client -o yaml
}

if $PRINT_ONLY; then
  render_secret
  exit 0
fi

if ! "\${KUBECTL[@]}" get namespace "$NAMESPACE" >/dev/null 2>&1; then
  die "namespace '$NAMESPACE' does not exist (create it first)"
fi

render_secret | "\${KUBECTL[@]}" apply -f -
echo "Secret '\${SECRET_NAME}' applied in namespace '\${NAMESPACE}' for \${SERVER}"

if [[ -n "$PATCH_SA" ]]; then
  current="$("\${KUBECTL[@]}" get serviceaccount "$PATCH_SA" -n "$NAMESPACE" \\
    -o jsonpath='{.imagePullSecrets[*].name}' 2>/dev/null)" \\
    || die "service account '$PATCH_SA' not found in '$NAMESPACE'"

  if [[ " $current " == *" $SECRET_NAME "* ]]; then
    echo "Service account '\${PATCH_SA}' already references '\${SECRET_NAME}'"
  elif [[ -z "$current" ]]; then
    "\${KUBECTL[@]}" patch serviceaccount "$PATCH_SA" -n "$NAMESPACE" --type merge \\
      -p "{\\"imagePullSecrets\\":[{\\"name\\":\\"\${SECRET_NAME}\\"}]}"
  else
    "\${KUBECTL[@]}" patch serviceaccount "$PATCH_SA" -n "$NAMESPACE" --type json \\
      -p "[{\\"op\\":\\"add\\",\\"path\\":\\"/imagePullSecrets/-\\",\\"value\\":{\\"name\\":\\"\${SECRET_NAME}\\"}}]"
  fi
fi`

const usageExamples = `# Prompt for the password interactively
./k8s-registry-secret.sh -s registry.lab.local -u julian -n myapp

# Pipe the password from a password manager and attach it to the default service account
pass show registry | ./k8s-registry-secret.sh -s registry.lab.local -u julian -P -a default

# Take the password from the environment, target another cluster
REGISTRY_PASSWORD=s3cret ./k8s-registry-secret.sh -s registry.lab.local -u julian -c staging

# Only render the Secret YAML (e.g. to commit it to a sealed-secrets / SOPS workflow)
./k8s-registry-secret.sh -s registry.lab.local -u julian -o`

const podSpecExample = `apiVersion: v1
kind: Pod
metadata:
  name: private-app
spec:
  containers:
    - name: app
      image: registry.lab.local/team/app:1.0.0
  imagePullSecrets:
    - name: regcred`

export default function RegistryPullSecret() {
    return (
        <div className={globalStyles.article}>
            <h1>Pull images from a private registry</h1>

            <p>To pull an image from a private container registry, Kubernetes needs a Secret of type
                <em> kubernetes.io/dockerconfigjson</em> holding the registry credentials, referenced from the
                pod (or its service account) through <em>imagePullSecrets</em>. The usual one-liner is:</p>
            <pre className={globalStyles.code}>{"kubectl create secret docker-registry regcred --docker-server={server} --docker-username={user} --docker-password={password}"}</pre>
            <p>It works, but the password ends up in your shell history and is visible to anyone running
                <em> ps</em> while the command runs, and running it twice fails because the secret already
                exists.</p>

            <h3>The script</h3>
            <p>This wrapper builds the <em>.dockerconfigjson</em> itself and pipes it to kubectl, so the
                password never appears on a command line. It uses <em>apply</em> instead of <em>create</em>,
                so re-running it updates the secret, and it can optionally add the secret to a service
                account so every pod using that account pulls with it automatically.</p>
            <ul>
                <li>Password from <em>-P</em> (stdin), the <em>REGISTRY_PASSWORD</em> env var, or an
                    interactive prompt.</li>
                <li><em>-c</em> to target a specific kubectl context, <em>-n</em> for the namespace.</li>
                <li><em>-a</em> to patch a service account&apos;s <em>imagePullSecrets</em> without
                    duplicating entries.</li>
                <li><em>-o</em> to only print the Secret YAML.</li>
            </ul>
            <div style={{maxWidth: "800px"}}>
                <SyntaxHighlighter
                    children={registrySecretScript}
                    style={dracula}
                    PreTag="div"
                    language={"bash"}
                />
            </div>

            <h3>Usage</h3>
            <div style={{maxWidth: "800px"}}>
                <SyntaxHighlighter
                    children={usageExamples}
                    style={dracula}
                    PreTag="div"
                    language={"bash"}
                />
            </div>

            <h3>Using the secret</h3>
            <p>If you patched the service account with <em>-a</em>, nothing else is needed: pods running
                under it get the pull secret injected. Otherwise reference it explicitly in the pod spec:</p>
            <div style={{maxWidth: "800px"}}>
                <SyntaxHighlighter
                    children={podSpecExample}
                    style={dracula}
                    PreTag="div"
                    language={"yaml"}
                />
            </div>
            <p>Secrets are namespaced, so the secret must exist in the same namespace as the pods that use
                it.</p>
        </div>
    )
}
